import Campaign from 'mini-campaign/lib/store/Campaign';
import LogDataType from 'telemetry/lib/schema/LogDataType';
import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import { CampaignBannerConfig } from 'mini-campaign/lib/campaign-types/CampaignBanner';
import { CampaignConfig } from 'mini-campaign/lib/store/schema/CampaignConfig';
import { CampaignEmbeddedMessageConfig } from 'mini-campaign/lib/campaign-types/CampaignEmbeddedMessage';
import { CampaignId } from 'mini-campaign-bootstrap/lib/schema/CampaignId';
import { CampaignModalConfig } from 'mini-campaign/lib/campaign-types/CampaignModal';
import { CampaignOpxHost } from 'mini-campaign/lib/campaign-types/CampaignOpxHost';
import { CampaignOverlayBannerConfig } from 'mini-campaign/lib/campaign-types/CampaignOverlayBanner';
import { CampaignOverlayChildConfig } from 'mini-campaign/lib/campaign-types/CampaignOverlayChild';
import { CampaignSnackBarConfig } from 'mini-campaign/lib/campaign-types/CampaignSnackBar';
import { CampaignTooltipConfig } from 'mini-campaign/lib/campaign-types/CampaignTooltip';
import { CampaignUnit } from 'mini-campaign/lib/components/CampaignUnit';
import { getCampaignObjectsCache } from 'mini-campaign/lib/utils/getCampaignObjectsCache';
import { getDataLogger } from 'telemetry';
import { getIrisActiveCampaignConfigs } from 'mini-campaign/lib/utils/getIrisActiveCampaigns';
import { isMiniHostAppCampaignEnabled } from 'mini-campaign-bootstrap/lib/utils/isMiniHostAppCampaignEnabled';
import { LazyCarouselSurface, lazySetCarouselCampaigns } from 'mini-campaign-carousel';
import { PlacementId } from 'mini-campaign-bootstrap/lib/schema/PlacementId';
import { setCampaignObjectsCache } from 'mini-campaign/lib/mutators/setCampaignObjectsCache';
import { shouldUseSdsIris } from 'sds-iris/lib/shouldUseSdsIris';
import { SurfaceConfig } from '../schema/SurfaceConfig';
import 'mini-campaign/lib/utils/registerCampaignIcons';

// Register all the icons which can be sent via iris config

const CAMPAIGN_IDS_TO_IGNORE = ['52']; // Blacklisted campaign ids

/**
 * Surface Component to render all the Campaign Units of the campaigns registered under a particular surface
 */
const Surface = (props: SurfaceConfig) => {
    const [activeCampaigns, setActiveCampaigns] = useState<Campaign<any>[]>([]);
    const {
        placementId,
        getRealtimeSignals,
        customCampaignRules = {},
        customCallbackFunctions = {},
        elementToFocusOnClose,
        elementToFocusOnOpen,
        isCarouselSurface = false,
        cacheCampaigns = false,
        setSurfaceHeight,
        customStyles = {},
    } = props;
    const logger: MutableRefObject<LogDataType> = useRef<LogDataType>(getDataLogger());

    useEffect(() => {
        const canStartCampaign =
            shouldUseSdsIris() && isMiniHostAppCampaignEnabled(placementId as PlacementId);
        if (canStartCampaign) {
            logger?.current?.('SURFACE_MOUNTED', { placementId });
            startCampaigns();
        }
    }, []);

    const startCampaigns = async () => {
        if (!cacheCampaigns) {
            activeCampaigns.push(...(await fetchActiveCampaignObjects()));
        } else {
            const cachedCampaignObjects = getCampaignObjectsCache(placementId);
            if (cachedCampaignObjects === undefined) {
                activeCampaigns.push(...(await fetchActiveCampaignObjects()));

                activeCampaigns?.length > 0 &&
                    setCampaignObjectsCache(placementId, activeCampaigns);
            } else {
                activeCampaigns.push(...cachedCampaignObjects);
            }
        }

        logger?.current?.('SURFACE_CAMPAIGNS_MOUNTED', { placementId });
        if (activeCampaigns) {
            setActiveCampaigns([...activeCampaigns]);

            if (isCarouselSurface) {
                lazySetCarouselCampaigns.importAndExecute(activeCampaigns);
            }
        }
    };

    const fetchActiveCampaignObjects = async (): Promise<Campaign<any>[]> => {
        const activeCampaignsObjects: Campaign<any>[] = [];
        const realtimeSignals: string = await getRealtimeSignals();
        let activeCampaignConfigs: CampaignConfig[] = await getIrisActiveCampaignConfigs(
            placementId,
            realtimeSignals
        );

        /**
         * Ignore campaigns from the active lists if it is in the black list
         * AND
         * Only include campaigns which are allowed by current host app (OutlookLite, Opx, Native, etc.).
         */
        activeCampaignConfigs = activeCampaignConfigs.filter(
            campaignConfig =>
                CAMPAIGN_IDS_TO_IGNORE.indexOf(campaignConfig.remoteCampaignId) < 0 &&
                isMiniHostAppCampaignEnabled(campaignConfig.remoteCampaignId as CampaignId)
        );

        activeCampaignConfigs?.forEach((campaignConfig: CampaignConfig) => {
            if (customCampaignRules) {
                if (campaignConfig.remoteCampaignId in customCampaignRules) {
                    campaignConfig.rule = customCampaignRules[campaignConfig.remoteCampaignId];
                }
            }
            if (customCallbackFunctions) {
                if (campaignConfig.remoteCampaignId in customCallbackFunctions) {
                    campaignConfig.renderProps = {
                        ...campaignConfig.renderProps,
                        ...customCallbackFunctions[campaignConfig.remoteCampaignId],
                    };
                }
            }

            if (customStyles) {
                if (campaignConfig.remoteCampaignId in customStyles) {
                    campaignConfig.renderProps.customStyles =
                        customStyles[campaignConfig.remoteCampaignId];
                }
            }
            campaignConfig.renderProps = {
                ...campaignConfig.renderProps,
                elementToFocusOnClose,
                elementToFocusOnOpen,
            };
            let campaign: Campaign<any>;
            switch (campaignConfig.renderProps.component) {
                case 'modal':
                    campaign = new Campaign<CampaignModalConfig>(campaignConfig);
                    break;
                case 'embeddedmessage':
                    campaign = new Campaign<CampaignEmbeddedMessageConfig>(campaignConfig);
                    break;
                case 'banner':
                    campaign = new Campaign<CampaignBannerConfig>(campaignConfig);
                    break;
                case 'overlaybanner':
                    campaign = new Campaign<CampaignOverlayBannerConfig>(campaignConfig);
                    break;
                case 'overlaychild':
                    campaign = new Campaign<CampaignOverlayChildConfig>(campaignConfig);
                    break;
                case 'opxhost':
                    campaign = new Campaign<CampaignOpxHost>(campaignConfig);
                    break;
                case 'snackbar':
                    campaign = new Campaign<CampaignSnackBarConfig>(campaignConfig);
                    break;
                case 'tooltip':
                default:
                    campaign = new Campaign<CampaignTooltipConfig>(campaignConfig);
                    break;
            }
            activeCampaignsObjects.push(campaign);
        });

        return activeCampaignsObjects;
    };
    return isCarouselSurface ? (
        <LazyCarouselSurface setSurfaceHeight={setSurfaceHeight} />
    ) : (
        <>
            {activeCampaigns.map((campaign, index) => (
                <CampaignUnit
                    key={'SurfaceCampaignUnit-' + index}
                    campaign={campaign}
                    viewProps={campaign.getRenderProps()}
                />
            ))}
        </>
    );
};

export default Surface;
